import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppTheme from "../../../theme/AppTheme";
import {
  CssBaseline,
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Alert,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  notifyError,
  notifySuccess,
} from "../../../components/ToastContainer/Toast/Toast";
import {
  getOfferById,
  updateOffer,
  uploadOfferBanner,
} from "../../../service/BuyerConfiguration/OfferManagement/offerService";

/**
 * Edit an existing offer.
 *
 * All fields are editable, including on a live offer. Edits apply to FUTURE
 * bookings only: promo_offer_claims snapshots amount_paid and delivery_charge
 * at confirm time, so a customer who already booked keeps the terms they
 * agreed to. Slots are the exception — they cannot drop below the number
 * already confirmed, because inventory is shared rather than per-claim.
 */
const LIVE_STATUSES = ["booking_open", "sold_out"];

const EditOffer = (props: { disableCustomTheme?: boolean }) => {
  const navigate = useNavigate();
  const { offerId } = useParams<{ offerId: string }>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string>("");

  const [name, setName] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [description, setDescription] = useState("");
  const [maxClaims, setMaxClaims] = useState<string>("");
  const [confirmedCount, setConfirmedCount] = useState(0);

  const [offerPrice, setOfferPrice] = useState<string>("");
  const [strikePrice, setStrikePrice] = useState<string>("");
  const [taxPercent, setTaxPercent] = useState<string>("");

  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);

  const isLive = LIVE_STATUSES.includes(status);

  useEffect(() => {
    if (!offerId) return;
    getOfferById(offerId)
      .then((res) => {
        const o = res?.data ?? res;
        const offer = o?.data ?? o;
        setName(offer.name ?? "");
        setSubtitle(offer.subtitle ?? "");
        setDescription(offer.description ?? "");
        setMaxClaims(String(offer.max_claims ?? ""));
        setOfferPrice(String(offer.offer_price ?? ""));
        setStrikePrice(offer.strike_price ? String(offer.strike_price) : "");
        setTaxPercent(
          offer.tax_percent === null || offer.tax_percent === undefined
            ? ""
            : String(offer.tax_percent),
        );
        setStatus(offer.status ?? "");
        setConfirmedCount(offer.confirmed_count ?? 0);
        setBannerPreview(offer.banner_image_url ?? null);
      })
      .catch(() => notifyError("Could not load this offer"))
      .finally(() => setLoading(false));
  }, [offerId]);

  const handleSave = async () => {
    if (!offerId) return;
    if (!name.trim()) {
      notifyError("Offer name is required");
      return;
    }
    if (Number(maxClaims) < confirmedCount) {
      notifyError(
        `Cannot reduce slots to ${maxClaims}: ${confirmedCount} are already booked.`,
      );
      return;
    }

    const payload: any = {
      name: name.trim(),
      subtitle: subtitle || undefined,
      description: description || undefined,
      max_claims: Number(maxClaims),
    };
    payload.offer_price = Number(offerPrice);
    payload.strike_price = strikePrice ? Number(strikePrice) : undefined;
    payload.tax_percent = taxPercent === "" ? undefined : Number(taxPercent);

    setSaving(true);
    try {
      await updateOffer(offerId, payload);

      if (bannerFile) {
        try {
          await uploadOfferBanner(offerId, bannerFile);
        } catch {
          notifyError("Offer saved, but the banner image failed to upload");
        }
      }

      notifySuccess("Offer updated");
      navigate("/offer-management");
    } catch (e: any) {
      notifyError(
        e?.response?.data?.message || e?.message || "Could not update this offer",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppTheme {...props}>
        <CssBaseline enableColorScheme />
        <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      </AppTheme>
    );
  }

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <Box sx={{ p: 2, maxWidth: 900 }}>
        <Typography variant="h5">Edit Offer</Typography>
        <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
          Status: {status.replace(/_/g, " ")}
        </Typography>

        {isLive && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This offer is live. Changes apply to future bookings only —
            customers who already booked keep the price and terms they paid.
            Slots cannot go below the number already confirmed.
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <TextField
            fullWidth
            required
            label="Offer Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Subtitle"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", mb: 2 }}>
            <Button variant="outlined" component="label">
              {bannerFile ? "Change Banner Image" : "Replace Banner Image"}
              <input
                hidden
                accept="image/*"
                type="file"
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  setBannerFile(file);
                  if (file) setBannerPreview(URL.createObjectURL(file));
                }}
              />
            </Button>
            {bannerPreview && (
              <Box
                component="img"
                src={bannerPreview}
                alt="Banner"
                sx={{
                  width: 180,
                  height: 90,
                  objectFit: "cover",
                  borderRadius: 1,
                  border: "1px solid #E5E7EB",
                }}
              />
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <TextField
            type="number"
            label="Max claims (slots)"
            helperText={
              confirmedCount > 0
                ? `${confirmedCount} already booked — cannot go below this`
                : "Total slots for this offer"
            }
            value={maxClaims}
            onChange={(e) => setMaxClaims(e.target.value)}
            sx={{ mb: 2 }}
          />

          <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  type="number"
                  label="Offer Price (₹)"
                  value={offerPrice}
                  onChange={(e) => setOfferPrice(e.target.value)}
                />
                <TextField
                  type="number"
                  label="Strike Price (₹)"
                  value={strikePrice}
                  onChange={(e) => setStrikePrice(e.target.value)}
                />
                <TextField
                  type="number"
                  label="Tax %"
                  helperText="Blank = normal tax · 0 = no tax"
                  value={taxPercent}
                  onChange={(e) => setTaxPercent(e.target.value)}
                />
              </Box>
            </>

          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save Changes"}
            </Button>
            <Button onClick={() => navigate("/offer-management")} disabled={saving}>
              Cancel
            </Button>
          </Box>
        </Paper>
      </Box>
    </AppTheme>
  );
};

export default EditOffer;
